import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Star } from '@phosphor-icons/react'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Select } from '@/components/ui/Select'
import { DotLoader } from '@/components/ui/DotLoader'
import { checkboxInputClass } from '@/lib/styleKit'
import { cn } from '@/lib/utils'
import { adminRatingsApi } from '../adminApi'
import { formatDate } from '../lib/labels'
import { RATING_WORD, formatAverage, insufficientMessage, starRows } from '../lib/ratingsPresenter'
import { EmptyState, PageHeader, Panel, PanelHeader, bodyStyle, metaStyle } from '../components/ui'

function Stars({ value, size = 13 }: { value: number; size?: number }) {
  return (
    <span className="inline-flex gap-px" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          weight={n <= value ? 'fill' : 'regular'}
          className={n <= value ? 'text-amber-500' : 'text-muted-foreground/30'}
        />
      ))}
    </span>
  )
}

export default function RatingsPage() {
  const [stars, setStars] = useState('')
  const [withReason, setWithReason] = useState(false)

  const summary = useQuery({
    queryKey: ['admin', 'ratings', 'summary'],
    queryFn: () => adminRatingsApi.summary().then((r) => r.data),
  })
  const list = useQuery({
    queryKey: ['admin', 'ratings', 'list', stars, withReason],
    queryFn: () =>
      adminRatingsApi
        .list({ stars: stars ? Number(stars) : undefined, with_reason: withReason || undefined, page_size: 100 })
        .then((r) => r.data),
    keepPreviousData: true,
  })

  const s = summary.data

  return (
    <div className="space-y-5">
      <PageHeader
        title="Ratings"
        description={s ? `${s.count} rating${s.count === 1 ? '' : 's'} · an average needs at least ${s.min_count}` : 'Loading…'}
      />

      {summary.isLoading && (
        <div className="py-14 flex justify-center">
          <DotLoader />
        </div>
      )}

      {s && !s.sufficient && (
        <Panel className="border-dashed">
          <EmptyState icon={<Star size={26} />}>{insufficientMessage(s)}</EmptyState>
        </Panel>
      )}

      {s && s.sufficient && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Panel>
            <PanelHeader title="Average" aside={`${s.count} ratings`} />
            <div className="p-4">
              <p className="text-foreground dark:text-[#f7f8f8] tabular-nums" style={{ fontSize: '40px', fontWeight: 590, letterSpacing: '-0.8px', lineHeight: 1.1 }}>
                {formatAverage(s.average)}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <Stars value={Math.round(s.average ?? 0)} size={16} />
                <span className="text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>
                  {RATING_WORD[Math.round(s.average ?? 0)] ?? ''}
                </span>
              </div>
            </div>
          </Panel>

          <Panel>
            <PanelHeader title="Distribution" />
            <ul className="p-4 space-y-2">
              {starRows(s).map((row) => (
                <li key={row.star} className="flex items-center gap-3" style={metaStyle}>
                  <span className="w-5 tabular-nums text-muted-foreground dark:text-[#8a8f98]">{row.star}★</span>
                  <div className="h-1.5 flex-1 rounded-full bg-muted dark:bg-white/[0.06] overflow-hidden">
                    <div className="h-full rounded-full bg-amber-500" style={{ width: `${row.percent}%` }} />
                  </div>
                  <span className="w-8 text-right tabular-nums text-muted-foreground dark:text-[#8a8f98]">{row.count}</span>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel>
            <PanelHeader title="Trend" aside="Weekly average" />
            <div className="p-4">
              {s.trend.length < 2 ? (
                <p className="py-6 text-center text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>
                  Not enough weeks yet for a trend.
                </p>
              ) : (
                <div className="h-28">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={s.trend} margin={{ top: 4, right: 4, bottom: 0, left: -28 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.08} vertical={false} />
                      <XAxis dataKey="week" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <YAxis domain={[1, 5]} ticks={[1, 3, 5]} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                      <Tooltip formatter={(v) => [Number(v ?? 0).toFixed(2), 'Average']} contentStyle={{ fontSize: 12 }} />
                      <Line type="monotone" dataKey="average" stroke="#5e6ad2" strokeWidth={2} dot={{ r: 2.5, strokeWidth: 0, fill: '#5e6ad2' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </Panel>
        </div>
      )}

      <Panel className="overflow-hidden">
        <PanelHeader
          title="Comments"
          aside={
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" className={checkboxInputClass} checked={withReason} onChange={(e) => setWithReason(e.target.checked)} />
                With a comment only
              </label>
              <Select
                ariaLabel="Stars"
                value={stars}
                onChange={setStars}
                options={[{ value: '', label: 'All stars' }, ...[5, 4, 3, 2, 1].map((n) => ({ value: String(n), label: `${n}★ ${RATING_WORD[n]}` }))]}
                className="w-36"
                buttonClassName="h-8"
              />
            </div>
          }
        />
        {list.isLoading && (
          <div className="py-10 flex justify-center">
            <DotLoader />
          </div>
        )}
        {list.data && list.data.items.length === 0 && <EmptyState>No ratings match.</EmptyState>}
        {list.data && list.data.items.length > 0 && (
          <ul className="divide-y divide-border/50 dark:divide-white/[0.06]">
            {list.data.items.map((item) => (
              <li key={item.id} className="px-4 py-3">
                <div className="flex items-center gap-2 text-muted-foreground dark:text-[#8a8f98]" style={metaStyle}>
                  <Stars value={item.rating} />
                  <span className="text-foreground dark:text-[#f7f8f8]">{RATING_WORD[item.rating]}</span>
                  <span>·</span>
                  <span className="truncate">{item.reporter.email ?? `user ${item.reporter.user_id}`}</span>
                  {item.reporter.account_name && <span className="truncate">· {item.reporter.account_name}</span>}
                  <span className="ml-auto whitespace-nowrap">{formatDate(item.created_at)}</span>
                </div>
                {item.reason && (
                  <p className={cn('mt-1.5 whitespace-pre-wrap text-foreground dark:text-[#f7f8f8]')} style={bodyStyle}>
                    {item.reason}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  )
}
